import type { MessageDescriptor } from "@formatjs/intl";
import { get } from "lodash";
import type { IntlShape } from "react-intl";
import { defineMessage, defineMessages } from "react-intl";

import type { PlatformConnection } from "../../components";
import type { Connector, ConnectorWithModal, Connectors } from "../../components/connector-content/interface";
import type { ConnectionPropertiesSchema, ConnectorPropertiesSchema } from "../schemas";
import { connectByPath } from "../services";
import type { ConnectorStates, TransformerFunction } from "../types";

const errorMessages = (key: string | undefined): MessageDescriptor | undefined => {
  if (!key) {
    return undefined;
  }

  return {
    "valid shopify URL hint": defineMessage({
      id: "shared-ui: valid shopify URL hint",
      defaultMessage: "Please enter a valid Shopify store name.",
    }),
    "valid woocommerce URL hint": defineMessage({
      id: "shared-ui: valid woocommerce URL hint",
      defaultMessage: "Unable to connect to your store. Please check the URL.",
    }),
    "valid woocommerce store hint": defineMessage({
      id: "shared-ui: valid woocommerce store hint",
      defaultMessage:
        "Unable to connect to your WooCommerce store as it sent us an unexpected response. Please contact support if the problem continues.",
    }),
    "woocommerce 403 hint": defineMessage({
      id: "shared-ui: woocommerce 403 hint",
      defaultMessage: "We couldn't connect to your store. Your firewall may be blocking our access.",
    }),
    "magento 403 hint": defineMessage({
      id: "shared-ui: magento 403 hint",
      defaultMessage: "We couldn't connect to your store. Your firewall may be blocking our access.",
    }),
    "valid bigcommerce access token hint": defineMessage({
      id: "shared-ui: valid bigcommerce access token hint",
      defaultMessage: "Please enter a valid access token for this store.",
    }),
    "valid bigcommerce otp hint": defineMessage({
      id: "shared-ui: valid bigcommerce otp hint",
      defaultMessage: "Please enter the code as shown in the Wayflyer BigCommerce app",
    }),
    "valid URL hint": defineMessage({
      id: "shared-ui: valid URL hint",
      defaultMessage: "Please enter a valid {platformName} store URL.",
    }),
  }[key];
};

/**
 * Utils
 *
 *
 * Retrieves an error from the server validation object keyed by the connector slug
 */
const errorMessage = (intl: IntlShape, slug, serverValidationErrors, field = "storeName", interpolations = {}) => {
  if (get(serverValidationErrors, `${slug}.errorField`) !== field) {
    return null;
  }
  const error = get(serverValidationErrors, `${slug}.errorMessage`);
  const errorLookup = errorMessages(error);
  return errorLookup ? intl.formatMessage(errorLookup, interpolations) : error;
};
/**
 * Optional protocol in the string
 */
// eslint-disable-next-line no-useless-escape
const StoreUrlRegExp = new RegExp(/^(?:https?:\/\/)?(?!www | www\.)[A-Za-z0-9_-]+\.+[A-Za-z0-9.\/%&=\?_:;-]+$/gm);

/**
 * 32 chars alphanumeric (lowercase)
 */
const MagentoTokenRegExp = new RegExp(/^[a-z0-9]{32}$/);

/**
 *  * 32 chars alphanumeric (uppercase)
 */
const PrestaShopKeyRegExp = new RegExp(/^[A-Z0-9]{32}$/);

/**
 * 60 chars and dashes
 */
const ShopifyStoreRegExp = new RegExp(/^[a-zA-Z0-9-]{1,61}$/);

/**
 * 31 chars alphanumeric (lowercase)
 */
const BigCommerceTokenRegExp = new RegExp(/^[a-zA-Z0-9]{24}$/);

/**
 * 40 chars alphanumeric
 */
const Shopware5APIKeyRegExp = new RegExp(/^[a-zA-Z0-9]{40}$/);

/**
 * Register functions to produce form configuration
 */
export const connectModalProps: { [key: string]: TransformerFunction } = {
  shopify2: (intl, { serverValidationErrors, shopifyPrivateApp }, state, slug) =>
    (state === "NOT_CONNECTED" || (state === "DISCONNECTED" && shopifyPrivateApp)) && {
      title: intl.formatMessage({
        id: "shared-ui: get connected - pre-connect - Connect Shopify account",
        defaultMessage: "Connect Shopify account",
      }),
      formFields: [
        ...(state === "NOT_CONNECTED"
          ? [
              {
                //only display storeurl when not connected
                label: intl.formatMessage({
                  id: "shared-ui: get connected - pre-connect - Shopify domain",
                  defaultMessage: "Shopify domain",
                }),
                name: "storeName",
                valuePrefix: "https://",
                valueSuffix: ".myshopify.com",
                placeholder: intl
                  .formatMessage({
                    id: "shared-ui: get connected - pre-connect - placeholder storeName.myshopify.com",
                    defaultMessage: "storename",
                  })
                  .toLowerCase(),
                initialValue: "",
                rules: [
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "shared-ui: valid shopify URL hint",
                      defaultMessage: "Please enter a valid Shopify store name.",
                    }),
                    pattern: ShopifyStoreRegExp,
                  },
                ],
                error: errorMessage(intl, slug, serverValidationErrors),
              },
            ]
          : []),
        ...(shopifyPrivateApp
          ? [
              {
                label: intl.formatMessage({
                  id: "shared-ui: get connected - pre-connect - Shopify Private App Password",
                  defaultMessage: "API Password",
                }),
                name: "shopifyPrivateAppPassword",
                type: "password",
                initialValue: "",
                rules: [
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "shared-ui: API key is required",
                      defaultMessage: "API key is required",
                    }),
                  },
                ],
                error: errorMessage(intl, slug, serverValidationErrors, "shopifyPrivateAppPassword"),
              },
            ]
          : []),
      ],
    },
  woocommerce2: (intl, { serverValidationErrors }, state, slug) => {
    const is403 = get(serverValidationErrors, `${slug}.errorMessage`) === "woocommerce 403 hint";
    return (
      state === "NOT_CONNECTED" && {
        title: intl.formatMessage({
          id: "shared-ui: get connected - pre-connect - Connect WooCommerce account",
          defaultMessage: "Connect WooCommerce account",
        }),
        formFields: [
          {
            label: intl.formatMessage({
              id: "shared-ui: get connected - pre-connect - WooCommerce domain",
              defaultMessage: "Store URL",
            }),
            name: "storeName",
            valuePrefix: "https://",
            placeholder: intl
              .formatMessage({
                id: "shared-ui: get connected - pre-connect - placeholder woocommerce storeName",
                defaultMessage: "mystorename.com",
              })
              .toLowerCase(),
            initialValue: "",
            rules: [
              {
                required: true,
                message: intl.formatMessage(
                  { id: "shared-ui: valid URL hint", defaultMessage: "Please enter a valid {platformName} store URL." },
                  { platformName: "WooCommerce" }
                ),
                pattern: StoreUrlRegExp,
              },
            ],
            error: errorMessage(intl, slug, serverValidationErrors, "storeName", { platformName: "WooCommerce" }),
          },
        ],
        ...(is403 && {
          link: {
            url: "https://help-center.wayflyer.com/en/articles/268930-how-to-configure-your-self-hosted-store-to-allowlist-api-calls-from-wayflyer",
            linkText: intl.formatMessage({
              id: "shared-ui: woocommerce 403 help link",
              defaultMessage: "Configure your store to allow API calls from Wayflyer",
            }),
            external: true,
          },
        }),
      }
    );
  },
  magento2: (intl, { connectorClickHandlers, serverValidationErrors }, state, slug) => {
    const is403 = get(serverValidationErrors, `${slug}.errorMessage`) === "magento 403 hint";
    return {
      title: intl.formatMessage({
        id: "shared-ui: get connected - pre-connect - Connect Magento account",
        defaultMessage: "Connect Magento account",
      }),
      formFields: [
        ...(state === "NOT_CONNECTED"
          ? [
              {
                label: intl.formatMessage({
                  id: "shared-ui: get connected - pre-connect - Magento domain",
                  defaultMessage: "Store URL",
                }),
                name: "storeName",
                valuePrefix: "https://",
                placeholder: intl
                  .formatMessage({
                    id: "shared-ui: get connected - pre-connect - placeholder magento storeName",
                    defaultMessage: "storename.com",
                  })
                  .toLowerCase(),
                initialValue: "",
                rules: [
                  {
                    required: true,
                    message: intl.formatMessage(
                      {
                        id: "shared-ui: valid URL hint",
                        defaultMessage: "Please enter a valid {platformName} store URL.",
                      },
                      { platformName: "Magento" }
                    ),
                    pattern: StoreUrlRegExp,
                  },
                ],
                error: errorMessage(intl, slug, serverValidationErrors, "storeName", { platformName: "Magento" }),
              },
            ]
          : []),
        {
          label: "Access Token",
          name: "magentoAccessToken",
          type: "password",
          placeholder: intl.formatMessage({
            id: "shared-ui: get connected - pre-connect - Magento Access Token placeholder",
            defaultMessage: "abcdefghijklmnopqrstuvwxyz012345",
          }),
          initialValue: "",
          rules: [
            {
              type: "string",
              required: true,
              message: intl.formatMessage({
                id: "shared-ui: valid Magento Token hint",
                defaultMessage: "Please enter a valid Magento Access Token.",
              }),
              pattern: MagentoTokenRegExp,
            },
          ],
          error: errorMessage(intl, slug, serverValidationErrors, "magentoAccessToken"),
        },
      ],
      link: is403
        ? {
            url: "https://help-center.wayflyer.com/en/articles/268930-how-to-configure-your-self-hosted-store-to-allowlist-api-calls-from-wayflyer",
            linkText: intl.formatMessage({
              id: "shared-ui: magento 403 help link",
              defaultMessage: "Configure your store to allow API calls from Wayflyer",
            }),
            external: true,
          }
        : {
            linkText: intl.formatMessage({
              id: "shared-ui: Get an access token",
              defaultMessage: "Get an access token",
            }),
            onClick: connectorClickHandlers.magento,
          },
    };
  },
  mockify: () => ({
    title: "Mockify\n(internal only)",
    formFields: [
      {
        label: "Store URL",
        name: "storeName",
        valuePrefix: "https://",
        placeholder: "mystorename.com",
        initialValue: "",
        rules: [
          {
            required: true,
            message: "Please enter a valid Mockify store URL.",
            pattern: StoreUrlRegExp,
          },
        ],
      },
    ],
  }),
  prestashop: (intl, { connectorClickHandlers, serverValidationErrors }, state, slug) => ({
    title: intl.formatMessage({
      id: "shared-ui: get connected - pre-connect - Connect PrestaShop account",
      defaultMessage: "Connect PrestaShop account",
    }),
    formFields: [
      ...(state === "NOT_CONNECTED"
        ? [
            {
              label: intl.formatMessage({
                id: "shared-ui: get connected - pre-connect - PrestaShop domain",
                defaultMessage: "Store URL",
              }),
              name: "storeName",
              valuePrefix: "https://",
              placeholder: intl
                .formatMessage({
                  id: "shared-ui: get connected - pre-connect - placeholder mystorename.com",
                  defaultMessage: "mystorename.com",
                })
                .toLowerCase(),
              initialValue: "",
              rules: [
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "shared-ui: valid URL hint- prestashop",
                    defaultMessage: "Please enter a valid PrestaShop store URL.",
                  }),
                  pattern: StoreUrlRegExp,
                },
              ],
              error: errorMessage(intl, slug, serverValidationErrors),
            },
          ]
        : []),
      {
        label: "Webservice Key",
        name: "prestashopWebserviceKey",
        type: "password",
        placeholder: intl.formatMessage({
          id: "shared-ui: get connected - pre-connect - PrestaShop Webservice Key placeholder",
          defaultMessage: "ABCDEFGHIJKLMNOPQRSTUVWXYZ01234",
        }),
        initialValue: "",
        rules: [
          {
            type: "string",
            required: true,
            message: intl.formatMessage({
              id: "shared-ui: valid PrestaShop Key hint",
              defaultMessage: "Please enter a valid PrestaShop Webservice Key",
            }),
            pattern: PrestaShopKeyRegExp,
          },
        ],
        error: errorMessage(intl, slug, serverValidationErrors, "prestashopWebserviceKey"),
      },
    ],
    link: {
      linkText: intl.formatMessage({ id: "shared-ui: Get a Webservice Key", defaultMessage: "Get a webservice key" }),
      onClick: connectorClickHandlers.prestashop,
    },
    showCodatDisclaimer: true,
  }),
  // eslint-disable-next-line no-empty-pattern
  amazonrutter: (intl, {}, state) => ({
    title: intl.formatMessage({
      id: "shared-ui: get connected - pre-connect - Connect Amazon account",
      defaultMessage: "Connect Amazon account",
    }),
    formFields: [
      ...(state !== "NOT_CONNECTED"
        ? []
        : [
            {
              type: "select",
              label: intl.formatMessage({
                id: "shared-ui: seller region - label",
                defaultMessage: "Amazon seller region",
              }),
              name: intl.formatMessage({ id: "shared-ui: seller region - name", defaultMessage: "name" }),
              description: intl.formatMessage({
                id: "shared-ui: seller region - description",
                defaultMessage: "description",
              }),
              options: [
                {
                  label: intl.formatMessage({
                    id: "shared-ui: seller region - North America",
                    defaultMessage: "North America",
                  }),
                  value: "na",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Canada", defaultMessage: "Canada" }),
                  value: "ca",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Mexico", defaultMessage: "Mexico" }),
                  value: "mx",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Brazil", defaultMessage: "Brazil" }),
                  value: "br",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Spain", defaultMessage: "Spain" }),
                  value: "sp",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - UK", defaultMessage: "UK" }),
                  value: "uk",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - France", defaultMessage: "France" }),
                  value: "fr",
                },
                {
                  label: intl.formatMessage({
                    id: "shared-ui: seller region - Netherlands",
                    defaultMessage: "Netherlands",
                  }),
                  value: "nl",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Germany", defaultMessage: "Germany" }),
                  value: "gr",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Italy", defaultMessage: "Italy" }),
                  value: "it",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Sweden", defaultMessage: "Sweden" }),
                  value: "se",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Poland", defaultMessage: "Poland" }),
                  value: "pl",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Turkey", defaultMessage: "Turkey" }),
                  value: "tr",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - India", defaultMessage: "India" }),
                  value: "in",
                },
                {
                  label: intl.formatMessage({
                    id: "shared-ui: seller region - Singapore",
                    defaultMessage: "Singapore",
                  }),
                  value: "sg",
                },
                {
                  label: intl.formatMessage({
                    id: "shared-ui: seller region - Australia",
                    defaultMessage: "Australia",
                  }),
                  value: "au",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - Japan", defaultMessage: "Japan" }),
                  value: "jp",
                },
                {
                  label: intl.formatMessage({ id: "shared-ui: seller region - U.A.E", defaultMessage: "U.A.E" }),
                  value: "ae",
                },
              ],
            },
          ]),
    ],
  }),
  bigcommercerutter: (intl, { connectorClickHandlers, serverValidationErrors }, state, slug) => ({
    title: intl.formatMessage({
      id: "shared-ui: get connected - pre-connect - Connect BigCommerce account",
      defaultMessage: "Connect BigCommerce account",
    }),
    formFields: [
      {
        label: intl.formatMessage({
          id: "shared-ui: get connected - pre-connect - BigCommerce OTP label",
          defaultMessage: "Enter the code displayed in the Wayflyer BigCommerce app",
        }),
        name: "otp",
        type: "string",
        initialValue: "",
        rules: [
          {
            type: "string",
            required: true,
            message: intl.formatMessage({
              id: "shared-ui: valid BigCommerce Token hint",
              defaultMessage: "Please enter the code as shown in the Wayflyer BigCommerce app",
            }),
            pattern: BigCommerceTokenRegExp,
          },
        ],
        error: errorMessage(intl, slug, serverValidationErrors, "otp", {
          platformName: "BigCommerce",
        }),
      },
    ],
    link: {
      linkText: intl.formatMessage({ id: "shared-ui: Get a code", defaultMessage: "Get a code" }),
      onClick: connectorClickHandlers.bigcommerce,
    },
    showCodatDisclaimer: true,
  }),
  shopware5: (intl, { connectorClickHandlers, serverValidationErrors }, state, slug) => ({
    title: intl.formatMessage({
      id: "shared-ui: get connected - pre-connect - Connect Shopware 5 account",
      defaultMessage: "Connect Shopware 5 account",
    }),
    formFields: [
      ...(state === "NOT_CONNECTED"
        ? [
            {
              label: intl.formatMessage({
                id: "shared-ui: get connected - pre-connect - Shopware 5 domain",
                defaultMessage: "Store URL",
              }),
              name: "storeName",
              valuePrefix: "https://",
              placeholder: intl
                .formatMessage({
                  id: "shared-ui: get connected - pre-connect - placeholder Shopware 5 storeName",
                  defaultMessage: "storename.com",
                })
                .toLowerCase(),
              initialValue: "",
              rules: [
                {
                  required: true,
                  message: intl.formatMessage(
                    {
                      id: "shared-ui: valid URL hint",
                      defaultMessage: "Please enter a valid {platformName} store URL.",
                    },
                    { platformName: "Shopware 5" }
                  ),
                  pattern: StoreUrlRegExp,
                },
              ],
              error: errorMessage(intl, slug, serverValidationErrors, "storeName", { platformName: "Shopware 5" }),
            },
          ]
        : []),
      {
        label: "Username",
        name: "username",
        placeholder: intl.formatMessage({
          id: "shared-ui: get connected - pre-connect - Shopware5 Username placeholder",
          defaultMessage: "user",
        }),
        initialValue: "",
        rules: [
          {
            required: true,
            message: intl.formatMessage({
              id: "shared-ui: Username is required",
              defaultMessage: "Username is required",
            }),
          },
        ],
        error: errorMessage(intl, slug, serverValidationErrors, "shopware5Username"),
      },

      {
        label: "API key",
        name: "shopware5APIKey",
        type: "password",
        placeholder: intl.formatMessage({
          id: "shared-ui: get connected - pre-connect - Shopware 5 API key placeholder",
          defaultMessage: "abcdefghijklmnopqrstuvwxyzABCDEFGH012345",
        }),
        initialValue: "",
        rules: [
          {
            type: "string",
            required: true,
            message: intl.formatMessage({
              id: "shared-ui: valid Shopware 5 API key hint",
              defaultMessage: "Please enter a valid Shopware 5 API key.",
            }),
            pattern: Shopware5APIKeyRegExp,
          },
        ],
        error: errorMessage(intl, slug, serverValidationErrors, "shopware5APIKey"),
      },
    ],
    link: {
      linkText: intl.formatMessage({ id: "shared-ui: Get an API key", defaultMessage: "Get an API key" }),
      onClick: connectorClickHandlers.shopware5,
    },
  }),
};

/**
 * Preps the data returned from the configured modal forms for submission to the server
 */
export const prepFormData = (slug, formData) => {
  switch (slug) {
    //Handle the varying rules related to protocols in front of store name strings
    case "shopify2":
      //Remove any protocol from the storename
      formData.storeName = "storeName" in formData ? formData.storeName.replace(/(^\w+:|^)\/\//, "") : null;
      break;
    case "woocommerce2":
    case "magento2":
      //Ensure protocol is https://
      formData.storeName = "storeName" in formData ? "https://" + formData.storeName.replace(/(^\w+:|^)\/\//, "") : "";
  }
  return formData;
};

/**
 * Connector States
 *
 * In multistore there is either NOT_CONNECTED or COMING_SOON, connections
 * are handled separately.
 *
 * TODO: refactor this to return ENABLED/DISABLED instead - will require
 * updates to Connector component
 */
const connectorState = (
  isEnabled: boolean | undefined,
  connections: ConnectionPropertiesSchema[],
  multipleConnectionsAllowed: boolean
): ConnectorStates => {
  if (!isEnabled) {
    return "COMING_SOON";
  }
  if (connections.length > 0 && !multipleConnectionsAllowed) {
    return "DISABLED";
  }
  return "NOT_CONNECTED";
};

export const connectorLabelTranslations = (intl: IntlShape, id: string): string => {
  const msg = defineMessages({
    "connections - connected": {
      id: "shared-ui: connections - connected",
      defaultMessage: "Connected",
      description: "Indicates the platform is connected to the app",
    },
    "connections - disconnected": {
      id: "shared-ui: connections - disconnected",
      defaultMessage: "Disconnected",
      description: "Indicates the platform has been disconnected from the app",
    },
  })[id];

  return msg ? intl.formatMessage(msg) : id;
};

// Parses the data sources to produce a list of connectors: one for each platform
export const multiStoreConnectorTransformer = (
  intl: IntlShape,
  connectByPost: (path, connectorSlug, formData) => void,
  connectors: ConnectorPropertiesSchema[],
  props: any
): Connectors => {
  return connectors.map(
    ({
      name,
      slug,
      category,
      isEnabled,
      postPath,
      connectPath,
      connections,
      multipleConnectionsAllowed,
      connectionLogoSrc,
    }): Connectors[number] => {
      const state = connectorState(isEnabled, connections, multipleConnectionsAllowed);
      const commonProps: any = {
        name,
        slug,
        category,
        state,
        connectionLogoSrc,
      };
      if (state === "DISABLED") {
        commonProps.disabledReason = intl.formatMessage({
          id: "shared-ui: multiple connections not allowed tooltip",
          defaultMessage:
            "You can only have one connection to this platform. If you need to connect more or replace the current one please contact support",
        });
      }
      ("");
      if (connectModalProps[slug]) {
        return {
          ...commonProps,
          ...connectModalProps[slug](intl, props, state, slug),
          connectClickHandler: (formData: any) => connectByPost(postPath, slug, formData),
        } as ConnectorWithModal;
      } else {
        return {
          ...commonProps,
          connectClickHandler: () => connectByPath(connectPath),
        } as Connector;
      }
    }
  );
};

// Can the BE tell us this in the props in future?
const reconnectionsUsingPost = ["magento2", "bigcommercerutter", "shopware5", "prestashop", "mockify"];

const transformConnection = (
  intl: IntlShape,
  connectByPost: (path, connectorSlug, formData) => void,
  props: any,
  connection: ConnectionPropertiesSchema,
  connector: ConnectorPropertiesSchema,
  showReconnections: boolean
): PlatformConnection => {
  const baseInfo: PlatformConnection = {
    name: connection.identifierName?.trim() || connector.name,
    connectorType: connector.name,
    tagData: {
      type: connection.isConnected ? "connected" : "disconnected",
      label: connection.isConnected
        ? connectorLabelTranslations(intl, "connections - connected")
        : connectorLabelTranslations(intl, "connections - disconnected"),
    },
  };

  if (showReconnections && !connection.isConnected) {
    // Re-connections may need a post or just a redirect to an auth_start view
    const state: ConnectorStates = "DISCONNECTED";
    if (reconnectionsUsingPost.includes(connector.slug)) {
      return {
        ...baseInfo,
        reconnectionData: {
          state,
          ...connector,
          connectClickHandler: (formData: any) => connectByPost(connection.reconnectPostPath, connector.slug, formData),
          ...connectModalProps[connector.slug](intl, props, state, connector.slug),
        },
      };
    } else {
      return {
        ...baseInfo,
        reconnectionData: {
          state,
          ...connector,
          connectClickHandler: () => connectByPath(connection.reconnectPath),
        },
      };
    }
  }
  return baseInfo;
};

const createConnectEntry = (
  intl: IntlShape,
  connectByPost: (path, connectorSlug, formData) => void,
  props: any,
  connector: ConnectorPropertiesSchema
): PlatformConnection => {
  const baseInfo: PlatformConnection = {
    name: connector.name || "",
    connectorType: connector.name,
  };
  const state = "NOT_CONNECTED";

  if (connectModalProps[connector.slug]) {
    return {
      ...baseInfo,
      reconnectionData: {
        state,
        ...connector,
        connectClickHandler: (formData: any) => connectByPost(connector.postPath, connector.slug, formData),
        ...connectModalProps[connector.slug](intl, props, state, connector.slug),
      },
    };
  } else {
    return {
      ...baseInfo,
      reconnectionData: {
        state,
        ...connector,
        connectClickHandler: () => connectByPath(connector.connectPath),
      },
    };
  }
};

/* Not used, can be adopted once https://github.com/wayflyer/wayflyer/pull/13424 is merged */
const transformCodatFileUploads = (
  connector: ConnectorPropertiesSchema,
  connection: ConnectionPropertiesSchema,
  intl
): PlatformConnection[] => {
  const fileNames = connection.connectionTypeProperties?.fileNames;

  return fileNames
    ? fileNames.map((filename) => {
        return {
          name: filename,
          connectorType: connector.name,
          tagData: {
            type: "connected",
            label: intl.formatMessage({
              id: "shared-ui: connections - uploaded",
              defaultMessage: "Uploaded",
              description: "Indicates the file has been uploaded",
            }),
          },
        };
      })
    : [];
};

// Parses the connections for each connector into a single list for the PlatformConnectionList component
export const multiStoreConnectionsTransformer = (
  intl: IntlShape,
  connectByPost: (path, connectorSlug, formData) => void,
  connectors: ConnectorPropertiesSchema[],
  props: any,
  showReconnections: boolean
): PlatformConnection[] => {
  const connectionsList: PlatformConnection[] = [];

  connectors.forEach((connector) => {
    connector.connections.forEach((connection) => {
      if (connector.name === "Codat File Uploads") {
        connectionsList.push(...transformCodatFileUploads(connector, connection, intl));
      } else {
        connectionsList.push(transformConnection(intl, connectByPost, props, connection, connector, showReconnections));
      }
    });
  });

  return connectionsList;
};

export const settingsConnectorTransformer = (
  intl: IntlShape,
  connectByPost,
  connectors: ConnectorPropertiesSchema[],
  props: any
): PlatformConnection[] => {
  const connectionsList: PlatformConnection[] = [];

  connectors.forEach((connector) => {
    if (connector.connections.length > 0) {
      connector.connections.forEach((connection) => {
        connectionsList.push(transformConnection(intl, connectByPost, props, connection, connector, true));
      });
    } else {
      connectionsList.push(createConnectEntry(intl, connectByPost, props, connector));
    }
  });

  return connectionsList;
};
